import React, { useState } from 'react'

import * as Yup from 'yup'
import clone from 'lodash/clone'
import { useAnalytics } from 'use-analytics'

import { makeStyles, withStyles } from '@mui/styles'
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'

import { Formik, Form, Field } from 'formik'
// import { TextField } from 'formik-material-ui'

import { submitSupportTicket } from '@/api/legacy'

import { useConfig } from '@/config'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'stretch',
    // backgroundColor: '#f0f',
  },
  control: {
    // backgroundColor: '#f0f',
    width: '100%',
    margin: theme.spacing(1, 0),
    '& label': {
      transform: 'translate(0, 0px) scale(1.0)',
      fontSize: 15,
    },
  },
  customInput: {
    width: '100%',
  },
}))

const BootstrapTextField = withStyles((theme) => ({
  root: {
    // '& label': {
    //   marginTop: theme.spacing(3),
    // },
    'label + &': {
      marginTop: theme.spacing(3),
    },
    width: '100%',
    margin: theme.spacing(4, 0, 2),
    '& label': {
      transform: 'translate(5px, -2rem)',
    },
    '& p.Mui-error': {
      marginLeft: 4,
    },
    input: {
      borderRadius: 4,
      border: '1px solid #ced4da',
      padding: '10px 12px',
    },
    // '& input:valid + fieldset': {
    //   borderColor: 'green',
    //   borderWidth: 2,
    // },
    // '& input:invalid + fieldset': {
    //   borderColor: 'red',
    //   borderWidth: 2,
    // },
    // '& input:valid:focus + fieldset': {
    //   borderLeftWidth: 6,
    //   padding: '4px !important', // override inline-style
    // },
  },
}))(TextField)

// Formik

const SupportFormSchema = Yup.object().shape({
  name: Yup.string().max(100, 'Name is too long'),
  email: Yup.string()
    .email('Invalid email address')
    .max(100, 'Email is too long')
    .required('An email address is required'),
  subject: Yup.string().max(100, 'Subject is too long'),
  description: Yup.string()
    .max(1024, 'Description is too long')
    .required('A description is required'),
})

const initialValues = {
  name: '',
  email: '',
  subject: '',
  description: '',
}

// Alert

const SubmitConfirmation = ({ open, onClose, title, message }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="submit-confirmation-title"
      aria-describedby="submit-confirmation-description"
    >
      <DialogTitle id="submit-confirmation-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="submit-confirmation-description">
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const SupportForm = () => {
  const { track } = useAnalytics()
  const classes = useStyles()
  const { tenantConfig } = useConfig()

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [confirmation, setConfirmation] = useState(null)

  const site =
    tenantConfig.options.freshdeskTitle ||
    tenantConfig.options.title ||
    'Untitled AMS Site'

  const handleReset = () => {}

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    track('supportFormSubmit', {
      category: 'Support',
      label: 'Submit',
    })

    setSubmitting(true)

    const ticket = {
      Site: site,
      FromName: values.name,
      FromEmail: values.email,
      Subject: values.subject,
      Message: values.description,
    }

    const res = await submitSupportTicket(ticket)

    setSubmitting(false)

    if (res) {
      setConfirmation({
        title: 'Support Ticket Submitted',
        message:
          'Thank you for submitting a support ticket. You should recieve a confirmation via email and our support staff will get in touch.',
      })
      setShowConfirmation(true)
      resetForm(clone(initialValues))
    } else {
      track('supportFormSubmitError', {
        category: 'Support',
        label: 'Error',
      })

      setConfirmation({
        title: 'Error Submitting Ticket',
        message:
          'An error occurred submitting a support ticket. Please try again later.',
      })
      setShowConfirmation(true)
    }
  }

  const CustomInputComponent = ({
    field, // { name, value, onChange, onBlur }
    form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
    ...props
  }) => (
    <div className={classes.customInput}>
      <BootstrapTextField {...field} {...props} />
      {touched[field.name] && errors[field.name] && (
        <div className="error" style={{ color: '#FF9494' }}>
          {errors[field.name]}
        </div>
      )}
    </div>
  )

  const handleCloseConfirmation = () => {
    setShowConfirmation(false)
  }

  return (
    <>
      <SubmitConfirmation
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        title={confirmation?.title}
        message={confirmation?.message}
      />

      <Formik
        initialValues={clone(initialValues)}
        validationSchema={SupportFormSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty, resetForm, isValid, submitForm, isSubmitting }) => (
          <Form className={classes.root}>
            <Field
              component={CustomInputComponent}
              name="name"
              type="text"
              label="Your name"
              variant="outlined"
              InputLabelProps={{
                shrink: false,
              }}
            />
            <Field
              component={CustomInputComponent}
              name="email"
              type="email"
              label="Your email *"
              variant="outlined"
              InputLabelProps={{
                shrink: false,
              }}
            />
            <Field
              component={CustomInputComponent}
              name="subject"
              type="subject"
              label="Subject"
              variant="outlined"
              InputLabelProps={{
                shrink: false,
              }}
            />

            <Field
              component={CustomInputComponent}
              name="description"
              type="string"
              label="Description *"
              variant="outlined"
              InputProps={{
                multiline: true,
                rows: 6,
              }}
              InputLabelProps={{
                shrink: false,
              }}
            />
            <Box
              sx={{
                width: '100%',
                mt: 2,
                display: 'inline-flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                sx={{ mr: 2 }}
                variant="contained"
                color="default"
                disableElevation
                disabled={isSubmitting}
                onClick={() => resetForm(clone(initialValues))}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                disabled={!(dirty && isValid && !isSubmitting)}
                onClick={submitForm}
              >
                Submit
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default SupportForm
